import { useState } from "react";
import { Question, CaretDown } from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import { useI18n } from "@/context/I18nContext";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

const FAQ_ITEMS = [
    {
        cat: "How PawLink works",
        q: "What is PawLink and how does the Smart QR pet ID work?",
        a: "PawLink is a digital pet identification and recovery service. We generate a unique digital Smart QR code linked to your pet's online profile. When a stranger scans the QR with their phone camera, they open a mobile-friendly page with your pet's photo, name, and a one-tap way to contact you — no app, no sign-up. You get an instant notification the moment the scan happens. PawLink does not sell, ship, or supply any physical product.",
    },
    {
        cat: "How PawLink works",
        q: "Does PawLink send me a physical tag?",
        a: "No. PawLink is a digital software subscription — we do not manufacture, sell, ship, or fulfill any physical product. Every pet gets a unique, high-resolution digital QR you can download from the pet's page and display however you like: print it at home, add it to any collar tag or accessory you already own, use it as a sticker, or share it digitally.",
    },
    {
        cat: "How PawLink works",
        q: "Does the finder need to install anything?",
        a: "No. Anyone with a smartphone camera can scan the QR. It opens directly in the browser. That is the point — the fastest possible path from finder to owner.",
    },

    {
        cat: "When someone finds my pet",
        q: "What happens when someone scans my pet's QR?",
        a: "The finder sees your pet's public profile: photo, name, breed, your chosen contact number, an optional voice message from you, and a Call or SMS button. You receive an in-app notification with the scan location and timestamp.",
    },
    {
        cat: "When someone finds my pet",
        q: "Can I record a voice message for whoever finds my pet?",
        a: "Yes. In each pet's Safety Center you can record a short voice message — a familiar voice can calm a scared animal while the finder waits with them. The finder plays it directly from the scan page.",
    },
    {
        cat: "When someone finds my pet",
        q: "What is Lost Mode?",
        a: "Lost Mode turns the scan page into an emergency profile with a large MISSING banner, your reward if any, and a plea from you. Owners on Protection get real-time GPS pings on every subsequent scan.",
    },
    {
        cat: "When someone finds my pet",
        q: "How accurate is the location on a scan?",
        a: "When the finder grants location permission, PawLink captures precise GPS coordinates (usually within 5–20 meters). If they decline, we fall back to an approximate area from their IP address — enough to know the neighborhood, not the address.",
    },

    {
        cat: "Privacy",
        q: "Can a finder see my home address?",
        a: "Never. Only the city and country you choose appear on the scan page — never the street address you entered in your account.",
    },
    {
        cat: "Privacy",
        q: "Can I hide my phone number from the public profile?",
        a: "Yes. You control per-pet what a finder sees. You can publish your phone, a burner number, an email, or an SMS-only channel — nothing is public unless you enable it.",
    },
    {
        cat: "Privacy",
        q: "Who can see my scan history?",
        a: "Only you. Scan history lives inside your Rescue Center and is never shown to a finder or any third party.",
    },
    {
        cat: "Privacy",
        q: "Does PawLink sell my data?",
        a: "No — never. See our Privacy Policy for the full explanation of what we collect and how it is used.",
    },

    {
        cat: "Subscription",
        q: "What is PawLink Protection?",
        a: "PawLink Protection is our optional premium plan. It unlocks unlimited pets, precise GPS on scans, the Digital Pet Passport, real-time push alerts, and priority support.",
    },
    {
        cat: "Subscription",
        q: "How much does it cost?",
        a: "9.90 CHF per month or 79.00 CHF per year. The annual plan works out to about 6.58 CHF a month — that's about 33% off the monthly price.",
    },
    {
        cat: "Subscription",
        q: "How do I cancel?",
        a: "Open the PawLink Protection page in your account. You can cancel with one tap; your Protection benefits continue until the end of the current billing period.",
    },
    {
        cat: "Subscription",
        q: "Do I need Protection to use PawLink?",
        a: "No. The free plan includes one pet with a Smart QR and basic finder contact. Protection is for owners who want unlimited pets, precise GPS, and the peace of mind of instant alerts.",
    },
];

export default function FAQ() {
    const { t } = useI18n();

    const grouped = FAQ_ITEMS.reduce((acc, item) => {
        (acc[item.cat] = acc[item.cat] || []).push(item);
        return acc;
    }, {});

    return (
        <LegalPageShell
            testId="faq-page"
            kicker={t("legal.faq_kicker")}
            title={t("legal.faq_title")}
            sub={t("legal.faq_sub")}
            icon={<Question size={28} weight="fill" />}
        >
            <p>
                Questions we hear from pet owners every week. If yours is not here, reach us any
                time on the <a href="/support">Contact Support</a> page or email{" "}
                <a href={supportMailto()} dir="ltr" data-testid="faq-support-email">{SUPPORT_EMAIL}</a>{" "}
                — we usually reply within one business day.
            </p>

            {Object.entries(grouped).map(([cat, items], gi) => (
                <div key={cat} className="mt-10">
                    <h2 className="!mt-0">{cat}</h2>
                    <div className="mt-4 rounded-3xl border border-paw-line dark:border-white/10 divide-y divide-paw-line dark:divide-white/10 overflow-hidden">
                        {items.map((it, i) => (
                            <FaqRow key={it.q || `faq-${gi}-${i}`} item={it} defaultOpen={gi === 0 && i === 0} testId={`faq-item-${gi}-${i}`} />
                        ))}
                    </div>
                </div>
            ))}
        </LegalPageShell>
    );
}

function FaqRow({ item, defaultOpen, testId }) {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <div data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full text-start flex items-center gap-3 px-5 sm:px-6 py-4 hover:bg-paw-muted/40 dark:hover:bg-white/5 transition"
            >
                <span className="flex-1 font-semibold text-paw-ink dark:text-paw-bone">{item.q}</span>
                <CaretDown
                    size={16}
                    weight="bold"
                    className={`text-paw-forest dark:text-paw-terra transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="px-5 sm:px-6 pb-5 -mt-1">
                    <p className="!mt-0 text-paw-ink2 dark:text-paw-bone/75 leading-relaxed">{item.a}</p>
                </div>
            )}
        </div>
    );
}
